<template>
    <div>
        <!-- Transfer generate button -->
        <div v-if="showTransferUi">
            <button
                type="button"
                :disabled="isUploading || isPendingTransfer"
                class="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-x-1 rounded-md bg-order-buttons px-3.5 text-base font-extrabold text-white transition outline-none hover:brightness-110 focus:outline-none"
                :class="{ 'cursor-not-allowed opacity-75': isUploading || isPendingTransfer }"
                @click="onTransferButtonClick"
            >
                <Spinner v-if="isUploading || isPendingTransfer" size="sm" size-override="size-6 md:size-6" class="mr-2.5" for-button />
                <SparklesIcon v-if="!isUploading && !isPendingTransfer" aria-hidden="true" class="mr-1.5 size-5" />
                {{ $t('pages.product_detail_page.ai_upload.transfer.generate') }}
            </button>
            <AiAuthModal v-model:open="showAuthModal" @login-success="onTransferLoginSuccess" />
        </div>

        <!--
            Order button: shown once a photo has been uploaded (session active), disabled while a
            generation is in flight. In the sheet footer (hideUntilTextSaved) a with-text product
            waits for an actual save — see showOrderButton — rather than warning on click.
        -->
        <template v-else-if="showOrderButton">
            <div @click.capture="onAddToCartClick">
                <ProductAddToCartButton ref="addToCartButtonRef" :disabled="isGeneratingInFlight" :suppress-loading-overlay="isPreparingCartFlow" />
            </div>

            <ActionStatus kind="aiAddToCart" :state="isPreparingCartFlow ? 'loading' : null" />
            <AddToCartErrorModal
                v-model:open="hasPreparationFailed"
                :description="$t('pages.product_detail_page.ai_upload.progress.preparing_photo_error')"
                @retry="prepareFirstBeautyShotAndAddToCart"
            />
        </template>

        <!--
            The sheet footer's order button stays hidden until a with-text product's personalisation
            is saved — this takes the same slot and offers the step that is actually missing, rather
            than a sentence about it. Disabled while generating, like the order button it replaces.
        -->
        <button
            v-else-if="showAddTextButton"
            type="button"
            :disabled="isGeneratingInFlight"
            class="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-x-1 rounded-md bg-order-buttons px-3.5 text-base font-extrabold text-white transition outline-none hover:brightness-110 focus:outline-none"
            :class="{ 'cursor-not-allowed opacity-75': isGeneratingInFlight }"
            @click="openTextEditor"
        >
            <TypeIcon aria-hidden="true" class="mr-1.5 size-5" />
            {{ addTextLabel }}
        </button>

        <!-- Regular upload UI: shown when no transfer is pending and no session is active -->
        <div v-else-if="!isMounted || !session.isActive" class="space-y-2">
            <template v-if="isDailyThresholdExceeded">
                <p class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    {{ $t('pages.product_detail_page.ai_upload.daily_limit_reached') }}
                </p>
            </template>

            <template v-else-if="isUserDailyLimitExceeded">
                <p class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    {{ userLimitMessage }}
                </p>
            </template>

            <template v-else>
                <button
                    type="button"
                    :disabled="isUploading || disabled"
                    class="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-x-1 rounded-md bg-order-buttons px-3.5 text-base font-extrabold text-white transition outline-none hover:brightness-110 focus:outline-none"
                    :class="{ 'cursor-not-allowed opacity-75': isUploading }"
                    @click="onButtonClick"
                >
                    <Spinner v-if="isUploading" size="sm" size-override="size-6 md:size-6" class="mr-2.5" for-button />
                    <!-- The same mark the AI badge and the regenerate block carry, rather than one about uploading. -->
                    <SparklesIcon v-if="!isUploading" aria-hidden="true" class="mr-1.5 size-6" />
                    {{
                        isAwaitingPhotoChoice
                            ? $t('pages.product_detail_page.ai_upload.button.choose_photo')
                            : $t('pages.product_detail_page.ai_upload.button.label')
                    }}
                </button>

                <p v-if="isOffline" class="text-sm text-amber-700">
                    {{ $t('pages.product_detail_page.ai_upload.button.offline') }}
                </p>

                <p v-else-if="isSlowConnection" class="text-sm text-amber-600">
                    {{ $t('pages.product_detail_page.ai_upload.button.slow_connection') }}
                </p>

                <div v-else-if="uploadErrorMessage" class="flex flex-col gap-1.5">
                    <p class="text-sm font-medium text-red-600">{{ uploadErrorMessage }}</p>
                    <button
                        type="button"
                        class="text-left text-sm font-medium text-primary underline underline-offset-2 hover:no-underline"
                        @click="open()"
                    >
                        {{ $t('pages.product_detail_page.ai_upload.button.try_again') }}
                    </button>
                </div>

                <AiAuthModal v-model:open="showAuthModal" @login-success="onLoginSuccess" @photo-picker-requested="onPhotoPickerRequested" />
            </template>
        </div>

        <!--
            The wait between choosing a photo and landing on the crop step. The button's own spinner
            is not enough on a phone: the picker covers the page, and the customer is wherever they
            had scrolled to when it closes — which after the USP row is nowhere near the button.

            Deliberately not `is-redirecting`. That swaps the copy for a second, "upload finished"
            message, which the Mediaclip flow earns because its redirect is a slow one — here the
            two run into each other and the swap reads as a flicker. The uploading copy already
            says both halves: the photo is going up, and you will be taken onward.
        -->
        <ActionStatus
            kind="aiUpload"
            :state="uploadStatusState"
            :progress="uploadProgress"
            :error-message="uploadFailureMessage"
            @retry="onUploadStatusRetry"
            @close="uploadFailureMessage = undefined"
        />
    </div>
</template>

<script setup lang="ts">
import ActionStatus from '@/components/status/ActionStatus.vue';
import type { StatusState } from '@/types/components/status/StatusTypes';
import Spinner from '@/components/spinners/Spinner.vue';
import ProductAddToCartButton from '@/pages/product-page/partials/product-info/partials/buttons/ProductAddToCartButton.vue';
import AddToCartErrorModal from '@/components/cart/partials/AddToCartErrorModal.vue';
import { useAppearanceComposable } from '@/composables/appearance/AppearanceComposable';
import { useAiCartBakeFlow } from '@/composables/ai-upload/useAiCartBakeFlow';
import { useAiCreditsRefresh } from '@/composables/ai-upload/useAiCreditsRefresh';
import { useAiCreditsRemaining } from '@/composables/ai-upload/useAiCreditsRemaining';
import { useAiCustomerAuth } from '@/composables/ai-upload/useAiCustomerAuth';
import { useAiPersonalizationControls } from '@/composables/ai-upload/useAiPersonalizationControls';
import { useAiTextEditorLauncher } from '@/composables/ai-upload/useAiTextEditorLauncher';
import { isPendingTransfer, openFilePickerTrigger, useAiPhotoTransfer } from '@/composables/ai-upload/useAiPhotoTransfer';
import { useTransferPreview } from '@/composables/useTransferPreview';
import { useAiPhotoUpload } from '@/composables/useAiPhotoUpload';
import { SparklesIcon, TypeIcon } from '@lucide/vue';
import AiAuthModal from '@/pages/ai-product-page/partials/ai-upload/modals/AiAuthModal.vue';
import { useConnectionGuard } from '@/composables/useConnectionGuard';
import { useAiGenerationHistoryStore } from '@/stores/ai-upload/AiGenerationHistoryStore';
import { useAiImageGenerationSessionStore } from '@/stores/ai-upload/AiImageGenerationSessionStore';
import { useAiPersonalizationStore } from '@/stores/ai-upload/AiPersonalizationStore';
import type { AppPageProps } from '@/types';
import type { ConfigurableProductPageObject } from '@/types/product';
import AiProductPageCreateProjectController from '@/types/wayfinder/actions/App/Http/Controllers/Web/ProductPage/AiProductPageCreateProjectController';
import AiProductPageUploadController from '@/types/wayfinder/actions/App/Http/Controllers/Web/ProductPage/AiProductPageUploadController';
import { router, usePage } from '@inertiajs/vue3';

type AiProductPageProps = AppPageProps & {
    product: ConfigurableProductPageObject;
};
import { useFileDialog } from '@vueuse/core';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

type ButtonProps = {
    disabled?: boolean;
    /**
     * The sheet footer has no room to explain an in-progress bake, so there the order button simply
     * waits for the text to be saved before it appears at all — rather than showing up and baking on
     * the first tap.
     */
    hideUntilTextSaved?: boolean;
};

const props = withDefaults(defineProps<ButtonProps>(), {
    disabled: false,
    hideUntilTextSaved: false,
});

const isMounted = ref(false);
const { transferPreview } = useTransferPreview();
const photoTransfer = useAiPhotoTransfer();
const personalizationStore = useAiPersonalizationStore();
const { onGenerationStarted } = useAiPersonalizationControls();
const { openTextEditor } = useAiTextEditorLauncher();

const addToCartButtonRef = ref<InstanceType<typeof ProductAddToCartButton>>();
const bypassUncommittedCheck = ref<boolean>(false);
const { isPreparingFirstBeautyShot, hasPreparationFailed, isAddingToCartAfterPreparation, isPreparingCartFlow, ensureFirstBeautyShotReady } =
    useAiCartBakeFlow();

/**
 * Bakes the first beauty shot for text that never got baked on the way past — see
 * useAiCartBakeFlow — then carries straight on into the cart. No confirmation step in between:
 * the customer already asked to order by pressing this button.
 *
 * isAddingToCartAfterPreparation is set before triggerClick() and only cleared once that button's
 * own request has actually failed (see the cartError watch below) — a real success navigates away
 * and takes this whole page with it, popup included, so there is nothing left here to reset.
 */
async function prepareFirstBeautyShotAndAddToCart(): Promise<void> {
    const isReady = await ensureFirstBeautyShotReady();

    if (!isReady) {
        return;
    }

    bypassUncommittedCheck.value = true;
    isAddingToCartAfterPreparation.value = true;
    addToCartButtonRef.value?.triggerClick();
}

/**
 * The button's own request failed — hand the popup card back down so the retry (the error modal's
 * own CTA, or another tap of the order button) starts from a clean state rather than a stuck-open
 * card from the attempt that just failed.
 */
watch(
    () => addToCartButtonRef.value?.cartError,
    hasError => {
        if (!hasError) {
            return;
        }

        isAddingToCartAfterPreparation.value = false;
    },
);

/**
 * The button's own request has settled, one way or the other. A real success navigates away and
 * takes this whole page with it before this ever runs — nothing left to reset there — but this is
 * what actually clears the flag rather than relying on that: a redirect that lands back on the same
 * component tree (Magento back(), a non-Inertia response) would otherwise leave the popup card, and
 * the module-level flag behind it, stuck open for the next visit.
 */
watch(
    () => addToCartButtonRef.value?.isAddingToCart,
    (isLoading, wasLoading) => {
        if (isLoading || wasLoading !== true) {
            return;
        }

        isAddingToCartAfterPreparation.value = false;
    },
);

async function onAddToCartClick(event: MouseEvent): Promise<void> {
    if (bypassUncommittedCheck.value) {
        bypassUncommittedCheck.value = false;
        return;
    }

    if (!personalizationStore.hasUncommittedChanges) {
        return;
    }

    event.stopPropagation();
    await prepareFirstBeautyShotAndAddToCart();
}

onMounted(() => {
    isMounted.value = true;
    initTransfer();
    startCarriedPhotoIfSwitched();

    // Module-level state (see useAiCartBakeFlow): a successful add-to-cart never gets to clear this
    // itself — the whole page, this component included, is torn down by the navigation before that
    // could run. A fresh mount is always a fresh visit, so it starts from a clean slate rather than
    // risking a stale "still adding to cart" left over from whichever page was here before.
    isAddingToCartAfterPreparation.value = false;
});

/**
 * The customer crossed between the halves of a with-text pair: they were looking at their result and
 * chose the other variant, so start from the photo they already gave us rather than showing a primed
 * button they have to tap again.
 *
 * Allowed to fire exactly once per switch, because a generation spends one of the customer's daily
 * credits and nothing refunds it. consumeVariantSwitch() removes the marker as it reads it, which is
 * what keeps the four mounted instances of this component from each starting a generation, and what
 * keeps a refresh or a back-button visit from starting another.
 *
 * Every reason to stop here — no marker, no carried photo, a guest, a limit reached — falls through to
 * the primed button, which is exactly the behaviour without any of this.
 */
function startCarriedPhotoIfSwitched(): void {
    if (transferPreview.value === undefined || !photoTransfer.consumeVariantSwitch()) {
        return;
    }

    if (!isCustomerAuthenticated.value || isDailyThresholdExceeded.value || isUserDailyLimitExceeded.value) {
        return;
    }

    onTransferButtonClick();
}

const page = usePage<AiProductPageProps>();
const session = useAiImageGenerationSessionStore();
const historyStore = useAiGenerationHistoryStore();
const { isTabletUp } = useAppearanceComposable();

const { isUploading, uploadProgress, uploadFile, onUploadError } = useAiPhotoUpload();

const { isCustomerAuthenticated, markLoggedInLocally } = useAiCustomerAuth();
const { refreshCreditsRemaining } = useAiCreditsRefresh();
const { isDailyThresholdExceeded, isUserDailyLimitExceeded, hoursUntilNextCredit, pendingCredits } = useAiCreditsRemaining();
const { t } = useI18n();

/**
 * Spells out the wait rather than saying "later". The allowance window rolls, so the exact hour
 * the next credit returns is knowable — see hoursUntilNextUserCredit(). Falls back to the vaguer
 * copy when the server could not work it out.
 */
const userLimitMessage = computed(() =>
    hoursUntilNextCredit.value === undefined
        ? t('pages.product_detail_page.ai_upload.user_limit_reached_unknown_wait')
        : t('pages.product_detail_page.ai_upload.user_daily_limit_reached', { count: hoursUntilNextCredit.value }),
);

const isGeneratingInFlight = computed<boolean>(
    () => session.isPersonalizationBaking || (session.isActive && session.step !== 'failed' && !session.isRevealGateOpen),
);

/*
 * On a with-text product the footer holds the add-text CTA until the customer has text of their own,
 * and the order button after — otherwise it would offer ordering the moment the generation landed,
 * before anyone had written anything.
 *
 * Saved rather than in the artwork: saving does not bake any more, so a customer who had written
 * their name and saved it could not reach the order button at all until they happened to open
 * another beauty shot. Ordering with text that is not baked yet is what the uncommitted-text modal
 * is for.
 *
 * And saved rather than merely written: the flag survives a reload with the text, so typing a name
 * and reloading without saving used to come back with the order button offered on it.
 */
const isTextNotYetSaved = computed<boolean>(
    () => props.hideUntilTextSaved && page.props.product.with_text === true && !personalizationStore.isTextSaved,
);

// Stays mounted mid-generation and goes disabled instead of disappearing: a without-text product
// has nothing else to wait for, so hiding it left the sheet footer empty for the whole generation.
// A with-text product is still gated by isTextNotYetSaved above.
const showOrderButton = computed<boolean>(() => isMounted.value && (session.isActive || historyStore.hasHistory) && !isTextNotYetSaved.value);

// The only reason the order button is not showing while a session/history exists is the
// isTextNotYetSaved gate above — this fills its slot instead, so the footer always holds a CTA.
const showAddTextButton = computed<boolean>(() => isMounted.value && (session.isActive || historyStore.hasHistory) && isTextNotYetSaved.value);

/*
 * "Bewerken" once there is something written, since the footer keeps this slot until the text has
 * been saved — so a customer who typed a name and left it unsaved was being asked to add one.
 */
const addTextLabel = computed<string>(() =>
    personalizationStore.hasOwnText
        ? t('pages.product_detail_page.ai_upload.button.edit_text')
        : t('pages.product_detail_page.ai_upload.button.add_text'),
);

// isPendingTransfer keeps this true across the upload: consuming the transfer clears the preview
// before the first await, so without it the button would unmount the instant it is tapped. The
// isGeneratingInFlight gate below is what hands over to the generation UI once the session activates.
const showTransferUi = computed<boolean>(
    () =>
        isMounted.value &&
        (transferPreview.value !== undefined || isPendingTransfer.value) &&
        !isGeneratingInFlight.value &&
        !session.isRevealGateOpen &&
        !isDailyThresholdExceeded.value &&
        !isUserDailyLimitExceeded.value &&
        !historyStore.hasHistory,
);

// Credits seeding, the staged-decrement reveal watcher, the Echo subscription and the beauty-shot
// poll all live in AiGenerationSessionHost — this component is rendered for one breakpoint at a
// time, so it cannot host state that has to outlive a viewport change.
const showAuthModal = ref<boolean>(false);

/** Signed in, with the photo still to choose — see onLoginSuccess. */
const isAwaitingPhotoChoice = ref<boolean>(false);
/** Chosen from inside the sign-in modal, before the sign-in had finished — see onLoginSuccess. */
const pendingPhotoFile = ref<File | undefined>(undefined);
/** The modal already opened the picker on its own submit tap — see onPhotoPickerRequested. */
const hasRequestedPhotoDuringAuth = ref<boolean>(false);
const uploadErrorMessage = ref<string | undefined>(undefined);
// True from the moment the project exists until the tool page has taken over, so the wait does
// not blink out between the upload finishing and the navigation starting.
const isOpeningTool = ref<boolean>(false);
// Scoped to this upload attempt: the inline message below the button survives a dismissal,
// but the blocking modal should only speak up for the attempt that actually failed.
const uploadFailureMessage = ref<string | undefined>(undefined);
const isOffline = ref<boolean>(false);
const isSlowConnection = ref<boolean>(false);

useConnectionGuard({
    isWaiting: isUploading,
    isShowingLoading: () => isUploading.value,
    isShowingOffline: () => isOffline.value,
    onOffline: () => {
        isOffline.value = true;
    },
    onReconnected: () => {
        isOffline.value = false;
    },
    onSlowConnection: () => {
        isSlowConnection.value = true;
    },
    onFastConnection: () => {
        isSlowConnection.value = false;
    },
});

const { open, onChange } = useFileDialog({
    accept: 'image/png,image/jpg,image/jpeg,image/webp',
    multiple: false,
    directory: false,
    reset: true,
});

watch(openFilePickerTrigger, (value: number) => {
    if (value > 0) {
        open();
    }
});

onUploadError(failure => {
    void refreshCreditsRemaining();

    uploadFailureMessage.value = failure.message;
    uploadErrorMessage.value = failure.message;
});

onChange(files => {
    const file = files?.[0];

    if (!file) {
        return;
    }

    isAwaitingPhotoChoice.value = false;
    uploadErrorMessage.value = undefined;

    // Picked while the sign-in behind the picker was still in flight: the upload endpoint would
    // turn it away, so it waits here and onLoginSuccess sends it the moment there is an account.
    if (!isCustomerAuthenticated.value) {
        pendingPhotoFile.value = file;

        return;
    }

    /*
     * The original goes up untouched. Cropping moved to the tool page, which means the server has
     * to hold the photo the customer actually chose — a crop applied here would be baked in, and
     * going back to redraw it would have nothing left to redraw from.
     */
    void uploadAndOpenTool(file);
});

function initTransfer(): void {
    const transfer = photoTransfer.peek();

    if (transfer === undefined) {
        return;
    }

    transferPreview.value = transfer.imageDataUrl;
}

function onTransferButtonClick(): void {
    if (isUploading.value || isPendingTransfer.value) {
        return;
    }

    if (!isCustomerAuthenticated.value) {
        showAuthModal.value = true;
        return;
    }

    isPendingTransfer.value = true;
    void uploadTransferredPhoto().finally(() => {
        isPendingTransfer.value = false;
    });
}

function onTransferLoginSuccess(): void {
    markLoggedInLocally();
    isPendingTransfer.value = true;
    void uploadTransferredPhoto().finally(() => {
        isPendingTransfer.value = false;
    });
}

async function uploadTransferredPhoto(): Promise<void> {
    const transfer = photoTransfer.consume();

    if (transfer === undefined) {
        transferPreview.value = undefined;
        return;
    }

    transferPreview.value = undefined;

    const file = photoTransfer.dataUrlToFile(transfer.imageDataUrl, transfer.imageFileName);

    // Re-save the transfer after consuming it: consume() removes the entry from storage, and the
    // photo has to stay available for the panel's photo row and for carrying to a related style.
    photoTransfer.restoreFromDataUrl(transfer.imageDataUrl, transfer.imageFileName, transfer.personalizationItems);

    // page.props.product is always current after Inertia navigation — use it instead
    // of productStore which may still reference the previous CP after a related-style
    // navigation via the slider.
    const transferProductSku = page.props.product.default_configurable_product_product.product.sku;

    await uploadAndActivate(file, transferProductSku);

    personalizationStore.reset();
    transfer.personalizationItems.forEach(item => {
        personalizationStore.items.push(item);
    });

    // Mark as committed so the SVG text overlay is not shown on top of beauty
    // shots that already have the text baked in from the previous generation.
    if (transfer.personalizationItems.length > 0) {
        personalizationStore.setCommittedHash('restored', transfer.personalizationItems);
    }
}

/**
 * Opens a project from the chosen photo and hands the customer to the tool.
 *
 * Nothing is generated here and no credit is spent: the customer has not cropped anything or
 * chosen any text yet, so there is nothing to generate from.
 */
/**
 * The blocking wait behind the upload button.
 *
 * An error outranks the loading state: a rejected photo used to say so only in a line of text
 * under the button, which on a phone is wherever the customer had scrolled to before the picker
 * covered the page — so the upload simply appeared to do nothing.
 */
const uploadStatusState = computed<StatusState>(() => {
    if (uploadFailureMessage.value !== undefined) {
        return 'error';
    }

    return isOpeningTool.value ? 'loading' : null;
});

function onUploadStatusRetry(): void {
    uploadFailureMessage.value = undefined;
    open();
}

async function uploadAndOpenTool(file: File): Promise<void> {
    const sku = page.props.product.default_configurable_product_product.product.sku;

    uploadFailureMessage.value = undefined;

    /*
     * Raised here and lowered only on failure, so one flag covers the whole journey to the crop
     * step. Deriving the wait from the uploader's own flag instead left a gap: it drops the moment
     * the request settles, and the redirect cannot be started until the await resumes a tick later
     * — long enough for the status to close and reopen.
     */
    isOpeningTool.value = true;

    const result = await uploadFile(file, AiProductPageCreateProjectController({ product: sku }).url, page.props.product.group_identifier).catch(
        () => null,
    );

    // onUploadError has already put the reason in uploadFailureMessage; a null here with nothing
    // set means the request itself fell over, which still deserves something on screen.
    if (result === null || result.status !== 'created') {
        isOpeningTool.value = false;
        uploadFailureMessage.value ??= t('pages.product_detail_page.product_info.product_button.upload.errors.generic');

        return;
    }

    if (result.redirectUrl === null) {
        isOpeningTool.value = false;
        uploadFailureMessage.value = t('pages.product_detail_page.product_info.product_button.upload.errors.generic');

        return;
    }

    router.visit(result.redirectUrl, {
        onError: () => {
            isOpeningTool.value = false;
            uploadFailureMessage.value = t('pages.product_detail_page.product_info.product_button.upload.errors.generic');
        },
    });
}

async function uploadAndActivate(file: File, productSku?: string): Promise<void> {
    const sku = productSku ?? page.props.product.default_configurable_product_product.product.sku;
    const result = await uploadFile(file, AiProductPageUploadController({ product: sku }).url, page.props.product.group_identifier).catch(() => null);

    if (result === null || result.status !== 'processing' || !result.correlationId || !result.token) {
        return;
    }

    // In this order: the snapshot keeps the text with the version it was made on, and only then does
    // the new generation get its own — see onGenerationStarted.
    historyStore.snapshotIfCompleted();
    historyStore.resetSelection();
    onGenerationStarted();
    session.activate(result.correlationId, result.token);

    // Store after activate() so session.beautyShots is already [] — the watcher
    // then only fires when the NEW session's first beauty shot completes.
    if (result.creditsRemaining !== undefined) {
        pendingCredits.value = result.creditsRemaining;
    }
    if (result.isUserDailyLimitExceeded !== undefined) {
        isUserDailyLimitExceeded.value = result.isUserDailyLimitExceeded;
        hoursUntilNextCredit.value = result.hoursUntilNextCredit ?? undefined;
    }
}

function onButtonClick(): void {
    if (isUploading.value) {
        return;
    }

    if (!isCustomerAuthenticated.value) {
        showAuthModal.value = true;
        return;
    }

    open();
}

/**
 * The sign-in form's own submit tap, borrowed to open the picker.
 *
 * Safari only opens a file picker from a real user gesture, and the sign-in round trip outlives it:
 * open() called once the request came back is ignored there, which is why the customer was left to
 * find this button and press it a second time. Called from inside the tap instead, the picker comes
 * up straight away and the sign-in finishes behind it — one tap fewer, on every platform.
 *
 * Nothing awaited between the tap and here, or the gesture is spent. See AiAuthForm.
 */
function onPhotoPickerRequested(): void {
    if (pendingPhotoFile.value !== undefined || isUploading.value) {
        return;
    }

    hasRequestedPhotoDuringAuth.value = true;
    open();
}

/**
 * Signed in. Either the photo is already waiting — picked over the top of the modal — and goes up
 * now, or the customer closed the picker without choosing one.
 *
 * Nothing reopens it in that second case: the picker was already offered on the tap that signed
 * them in, and putting it back in front of someone who just dismissed it is a fight. The button
 * relabels to the one tap that is left instead.
 */
function onLoginSuccess(): void {
    markLoggedInLocally();

    const file = pendingPhotoFile.value;
    pendingPhotoFile.value = undefined;

    if (file !== undefined) {
        void uploadAndOpenTool(file);

        return;
    }

    isAwaitingPhotoChoice.value = true;

    if (hasRequestedPhotoDuringAuth.value) {
        return;
    }

    open();
}
</script>
